import { SandTableMapContainer } from '@components/SandTableMapContainer';
import { MEDIA_DOMAIN, PUBLIC_DOMAIN } from '@constants';
import { MainPlayer } from '@stores/krpPlayerStores';
import {
  type STMap,
  type STMapMarkerScene,
  type SceneData,
  SceneType,
} from '@types';
import classNames from 'classnames';
import produce from 'immer';
import { useEffect, useRef } from 'react';
import { stSceneAmap } from '../../../sandTable/STAMap';
import { isSTMarkerScene } from '../../../sandTable/utils/markerTypeCheck';
import { onPlayerViewChange } from '../../actions/viewActions';

interface Props {
  data: STMap | null;
  scene: SceneData;
}
export function MapContainer({ data, scene }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const north = scene.north || 0;

  const getActiveMarker = (): STMapMarkerScene | undefined => {
    if (!data) return;
    const marker = data.markers.find(
      (x): x is STMapMarkerScene =>
        isSTMarkerScene(x) && x.data.sceneId === scene.id
    );
    if (!marker) return;
    return marker;
  };

  useEffect(() => {
    return () => {
      stSceneAmap.removeMap();
    };
  }, []);

  useEffect(() => {
    if (scene.type === SceneType.pano && getActiveMarker()) {
      return onPlayerViewChange.watch((view) => {
        changeRadarAngle(view.hlookat);
      });
    }
  }, [data]);

  useEffect(() => {
    if (containerRef.current && data) {
      const selectedMarker = getActiveMarker();
      stSceneAmap.removeMap();
      stSceneAmap.init({
        mediaDomain: MEDIA_DOMAIN,
        publicDomain: PUBLIC_DOMAIN,
        target: containerRef.current,
        data: produce(data, (draft) => {
          draft.initialView = { type: 0 };
        }),
        showSelectedOnly: !!data.showCurrentSceneOnly,
        selectedId: selectedMarker?.id,
        markerSize: 14,
        radarSize: 80,
        hideTitle: true,
        onReady: () => {
          if (selectedMarker) {
            changeRadarAngle(MainPlayer.get()?.getView()?.hlookat);
            stSceneAmap.setCenter({
              lat: selectedMarker.icon.lat,
              lng: selectedMarker.icon.lng,
            });
          }
        },
        ...(selectedMarker && {
          onRadarRotate: (angle) => {
            MainPlayer.get()?.updateView({ hlookat: angle + north });
          },
        }),
      });
    }
  }, [data]);

  const changeRadarAngle = (ath = 0) => {
    stSceneAmap.updateAngle((ath % 360) - north);
  };

  return (
    <SandTableMapContainer
      className={classNames('absolute inset-0', { hidden: !data })}
      ref={containerRef}
    />
  );
}
